import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { ROLE_LABELS } from '../lib/types';
import type { AuthenticatorEnrolment } from '../lib/types';
import { ThemeToggle } from '../components/ThemeToggle';
import { PasswordField } from '../components/PasswordField';

export function SettingsPage() {
  const { session } = useSession();
  if (!session) return null;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="eyebrow">Your account</div>
          <h1>Settings</h1>
          <p className="subtitle">
            Appearance, two-step sign-in, password and the access you currently hold.
          </p>
        </div>
      </div>

      <div className="stack" style={{ maxWidth: 640 }}>
        <div className="card">
          <div className="card-head">
            <h2>Appearance</h2>
          </div>
          <div className="card-pad">
            <p className="muted mt-0" style={{ marginTop: 0 }}>
              Choose light or dark, or follow your operating system. The choice is saved to your
              account, so it follows you to another browser.
            </p>
            <div style={{ maxWidth: 320 }}>
              <ThemeToggle />
            </div>
          </div>
        </div>

        <GitlabTokenCard />

        <TwoStepCard />
        <ChangePasswordCard />

        <div className="card">
          <div className="card-head">
            <h2>Your access</h2>
          </div>
          <div className="card-pad">
            <div className="stack-sm">
              <Row label="Name" value={session.person.name} />
              <Row label="Email" value={session.person.email} />
              {session.person.jobTitle && <Row label="Job title" value={session.person.jobTitle} />}
              <Row
                label="Account type"
                value={session.person.isAdmin ? 'Administrator' : 'Standard'}
              />
            </div>

            <div className="field-label mt-2">
              Companies · <Link to="/roles" style={{ textDecoration: 'underline' }}>what these roles mean</Link>
            </div>
            {session.companies.length === 0 ? (
              <p className="dim">No company access yet.</p>
            ) : (
              <div className="row wrap" style={{ gap: 6 }}>
                {session.companies.map((company) => (
                  <span key={company.id} className="badge">
                    {/*
                      No role means a company reached rather than belonged to — the billing hat
                      can invoice it and do nothing else there. Saying "billing only" is the
                      honest label; the role list has nothing to offer for it.
                    */}
                    {company.name} · {company.role ? ROLE_LABELS[company.role] : 'billing only'}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * A personal GitLab token, so approvals and merges made through the Hub are attributed to the
 * real person in GitLab's own history rather than to a shared account.
 */
function GitlabTokenCard() {
  const { session, refresh } = useSession();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const has = !!session?.person.hasGitlabToken;

  async function save(value: string, message: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.patch('/me/preferences', { gitlabToken: value });
      setToken('');
      await refresh();
      setNotice(message);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save your token');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>GitLab</h2>
        <span className={has ? 'badge badge-ok' : 'badge'}>{has ? 'connected' : 'not connected'}</span>
      </div>
      <div className="card-pad">
        {error && <div className="callout">{error}</div>}
        {notice && <div className="callout callout-ok">{notice}</div>}

        <p className="muted" style={{ marginTop: 0 }}>
          Paste a personal access token with the <span className="mono">api</span> scope. Merges and
          approvals you make from the Hub are then recorded in GitLab as you, not as a shared
          account. The token is stored on the server and never sent back to the browser.
        </p>

        <label className="field">
          <span className="field-label">{has ? 'Replace token' : 'Personal access token'}</span>
          <PasswordField
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="glpat-…"
            autoComplete="off"
          />
          <span className="field-hint">
            Create one at GitLab → Preferences → Access tokens. Scope: <span className="mono">api</span>.
          </span>
        </label>

        <div className="row">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || !token.trim()}
            onClick={() => save(token.trim(), 'GitLab token saved.')}
          >
            {busy ? 'Saving…' : 'Save token'}
          </button>
          {has && (
            <button type="button" className="btn btn-danger" disabled={busy} onClick={() => save('', 'GitLab token removed.')}>
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row-between">
      <span className="muted">{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setDone(false);

    if (newPassword !== confirm) {
      setError('The new password and its confirmation do not match');
      return;
    }

    setBusy(true);
    try {
      await api.patch('/auth/password', { currentPassword, newPassword });
      setDone(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirm('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change your password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Password</h2>
      </div>
      <div className="card-pad">
        {error && <div className="callout">{error}</div>}
        {done && <div className="callout callout-ok">Your password has been changed.</div>}

        <form onSubmit={submit} noValidate>
          <label className="field">
            <span className="field-label">Current password</span>
            <PasswordField
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </label>

          <label className="field">
            <span className="field-label">New password</span>
            <PasswordField
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <span className="field-hint">
              At least 10 characters, using 3 of: lowercase, uppercase, digit, symbol.
            </span>
          </label>

          <label className="field">
            <span className="field-label">Confirm new password</span>
            <PasswordField
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>

          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Changing…' : 'Change password'}
          </button>
        </form>
      </div>
    </div>
  );
}

/**
 * Two-step sign-in, off unless somebody turns it on.
 *
 * Every change here asks for the current password. Turning it OFF with a session somebody
 * else has got hold of would remove the very thing standing in their way; turning it ON
 * without proving who you are would lock the real owner out of their own account. Same
 * reasoning both directions, so the same requirement.
 */
function TwoStepCard() {
  const { session, refresh } = useSession();
  const mode = session?.person.twoFactorMode ?? 'off';
  /** The one situation with no recovery path inside the app; see the warning below. */
  const soleAdmin = !!session?.person.isAdmin && session.activeAdminCount <= 1;

  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  /** Set while an authenticator is being set up but not yet proved. */
  const [enrolment, setEnrolment] = useState<AuthenticatorEnrolment | null>(null);
  const [code, setCode] = useState('');

  async function act(what: () => Promise<unknown>, success: string) {
    if (!password) {
      setError('Enter your current password to change this.');
      return;
    }
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await what();
      await refresh();
      setDone(success);
      setPassword('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Two-step sign-in</h2>
        <span className={mode === 'off' ? 'badge' : 'badge badge-ok'}>
          {mode === 'off' ? 'off' : mode === 'app' ? 'authenticator app' : 'emailed code'}
        </span>
      </div>
      <div className="card-pad stack-sm">
        <p className="dim" style={{ margin: 0, fontSize: 13 }}>
          A second step after your password. An emailed code needs nothing set up; an
          authenticator app is stronger, because the code never travels anywhere.
        </p>

        {error && <div className="callout">{error}</div>}
        {done && <div className="callout callout-ok">{done}</div>}

        <label className="field" style={{ marginBottom: 0, maxWidth: 320 }}>
          <span className="field-label">Current password</span>
          <PasswordField
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        {!enrolment ? (
          <div className="row wrap" style={{ gap: 7 }}>
            {mode !== 'email' && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy}
                onClick={() => void act(() => api.patch('/auth/two-factor', { mode: 'email', password }), 'Codes will now be emailed to you when you sign in.')}
              >
                Email me a code
              </button>
            )}

            {mode !== 'app' && (
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={busy || !password}
                onClick={async () => {
                  setError(null);
                  setDone(null);
                  setBusy(true);
                  try {
                    setEnrolment(await api.post<AuthenticatorEnrolment>('/auth/two-factor/authenticator', { password }));
                  } catch (err) {
                    setError(err instanceof ApiError ? err.message : 'Could not start the setup.');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Use an authenticator app
              </button>
            )}

            {mode !== 'off' && (
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={busy}
                onClick={() => void act(() => api.patch('/auth/two-factor', { mode: 'off', password }), 'Two-step sign-in is off.')}
              >
                Turn it off
              </button>
            )}
          </div>
        ) : (
          <div className="stack-sm">
            <p style={{ margin: 0, fontSize: 13 }}>
              Scan this with your authenticator, then enter the code it shows to prove it works.
              Nothing changes until you do — an enrolment that was never proved is how people
              lock themselves out.
            </p>

            <div className="row wrap" style={{ gap: 18, alignItems: 'flex-start' }}>
              {/*
                The QR is the normal path — pointing a phone at it beats typing thirty-two
                characters into a keypad. The SVG comes from our own server, not from anything
                a user supplied, which is what makes setting it as markup acceptable here.
              */}
              <div
                className="qr-frame"
                aria-label="QR code for your authenticator app"
                dangerouslySetInnerHTML={{ __html: enrolment.qr }}
              />

              <div className="grow" style={{ minWidth: 220 }}>
                <label className="field" style={{ marginBottom: 0 }}>
                  <span className="field-label">Or type this key in by hand</span>
                  <input
                    readOnly
                    value={enrolment.secret}
                    onFocus={(event) => event.currentTarget.select()}
                    style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', letterSpacing: 1 }}
                  />
                </label>
                <p className="dim" style={{ fontSize: 12, marginTop: 6 }}>
                  Scan the code with Google Authenticator, Microsoft Authenticator, Authy or
                  1Password. The key is there for a phone that cannot scan.
                </p>
              </div>
            </div>

            <label className="field" style={{ marginBottom: 0, maxWidth: 220 }}>
              <span className="field-label">Code from the app ({enrolment.digits} digits)</span>
              <input
                value={code}
                inputMode="numeric"
                maxLength={enrolment.digits + 2}
                placeholder={'0'.repeat(enrolment.digits)}
                onChange={(event) => setCode(event.target.value.replace(/[^\d]/g, ''))}
                style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace', fontSize: 18, letterSpacing: 4 }}
              />
            </label>

            <div className="row" style={{ gap: 7 }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={busy || code.length < enrolment.digits}
                onClick={() =>
                  void act(async () => {
                    await api.post('/auth/two-factor/authenticator/confirm', { code, password });
                    setEnrolment(null);
                    setCode('');
                  }, 'Your authenticator is set up. It will be asked for next time you sign in.')
                }
              >
                Confirm
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => {
                  setEnrolment(null);
                  setCode('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {mode === 'app' && !soleAdmin && (
          <p className="dim" style={{ margin: 0, fontSize: 12 }}>
            Lost the phone? An administrator can turn this off for you from the People page —
            there is no recovering the app itself, which is rather the point of it.
          </p>
        )}

        {/*
          The one case the application genuinely cannot recover from. Somebody else's
          authenticator can always be cleared by an administrator; the SOLE administrator's
          cannot, because there is nobody left with the standing to do it. Said before they
          switch it on, not after.
        */}
        {soleAdmin && (
          <div className="callout">
            <strong>You are the only active administrator.</strong> Nobody else can clear your
            two-step sign-in, so if you lose your authenticator there is no way back through
            the app. Either make somebody else an administrator first, or keep this to hand:
            <div
              className="mono"
              style={{ marginTop: 8, fontSize: 12, userSelect: 'all' }}
            >
              npm run 2fa:clear -- {session?.person.email}
            </div>
            <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>
              That runs against the database and needs its credentials, so it works even when
              you cannot sign in. An emailed code has no such problem — the mailbox is the way
              back.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
